import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import {
  profileUpdateSchema,
  userProfileSchema,
  type ProfileUpdate,
  type SharingChoice,
  type UserProfile,
} from "./schemas";

export type ProfileState =
  | { status: "loading" }
  | { status: "not-provisioned" }
  | { status: "error"; error: string }
  | { status: "success"; profile: UserProfile };

const PROVISIONING_ATTEMPTS = 10;
const PROVISIONING_INTERVAL_MS = 750;

/**
 * Ask the backend to create this user's profile, but only if it needs asking.
 *
 * Firebase gen2 Python has no post-create Auth trigger, so the profile is
 * created by a trigger on this one-shot command document instead, and the
 * client is what files it.
 *
 * Both existence checks matter. The command is immutable — create is allowed,
 * update is not — so writing it unconditionally is a guaranteed
 * permission-denied on every sign-in after the first. That is not just noise:
 * a rejected write disturbs other writes in flight on the same connection,
 * which is how this first showed up, as an unrelated document create failing
 * in the next test.
 */
export async function requestProvisioning(uid: string): Promise<void> {
  try {
    const profile = await getDoc(doc(db, "users", uid));
    if (profile.exists()) return;

    const pending = await getDoc(doc(db, "user_provisioning_requests", uid));
    if (pending.exists()) return;

    await setDoc(doc(db, "user_provisioning_requests", uid), {
      uid,
      status: "REQUESTED",
      requestedAt: serverTimestamp(),
    });
  } catch {
    // A racing tab filed it first, or the read was refused. Either way the
    // trigger is the only thing that can finish provisioning.
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useUserProfile(uid: string): {
  state: ProfileState;
  reload: () => void;
} {
  const [state, setState] = useState<ProfileState>({ status: "loading" });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState({ status: "loading" });
      try {
        let snap = await getDoc(doc(db, "users", uid));
        if (cancelled) return;

        // A first sign-in has no profile yet. File the provisioning command
        // and wait for the trigger, rather than showing a dead end the user
        // has no way out of.
        if (!snap.exists()) {
          await requestProvisioning(uid);
          for (let attempt = 0; attempt < PROVISIONING_ATTEMPTS; attempt++) {
            await wait(PROVISIONING_INTERVAL_MS);
            if (cancelled) return;
            snap = await getDoc(doc(db, "users", uid));
            if (cancelled) return;
            if (snap.exists()) break;
          }
        }

        if (!snap.exists()) {
          setState({ status: "not-provisioned" });
          return;
        }

        const result = userProfileSchema.safeParse(snap.data());
        if (!result.success) {
          setState({ status: "error", error: "Invalid profile data" });
          return;
        }

        setState({ status: "success", profile: result.data });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          error: err instanceof Error ? err.message : "Failed to load profile",
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [uid, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { state, reload };
}

export function useUpdateProfile(uid: string) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useCallback(
    async (data: ProfileUpdate): Promise<boolean> => {
      const result = profileUpdateSchema.safeParse(data);
      if (!result.success) {
        setError("Invalid profile data");
        return false;
      }

      setSaving(true);
      setError(null);

      try {
        await updateDoc(doc(db, "users", uid), result.data);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to save profile");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [uid],
  );

  return { save, saving, error };
}

/**
 * Write the photo on its own, the moment it is uploaded. Left to the form's
 * Save button, an uploaded photo was lost by anyone who uploaded and then left
 * the page, and the object stayed behind in Storage with nothing pointing at it.
 */
export function useSaveAvatar(uid: string) {
  return useCallback(
    async (avatar: string): Promise<string | null> => {
      try {
        await updateDoc(doc(db, "users", uid), { avatar });
        return null;
      } catch (err) {
        return err instanceof Error ? err.message : "Failed to save photo";
      }
    },
    [uid],
  );
}

/**
 * Write one sharing choice the moment it is switched. A privacy switch that
 * waits for Save reads as done when it is not, so it saves on its own, as the
 * photo does. The backend copies it to every roster row linked to this
 * account, and the directory projection applies it there.
 */
export function useSaveSharing(uid: string) {
  return useCallback(
    async (choice: SharingChoice, value: boolean): Promise<string | null> => {
      try {
        await updateDoc(doc(db, "users", uid), { [choice]: value });
        return null;
      } catch (err) {
        return err instanceof Error ? err.message : "Failed to save";
      }
    },
    [uid],
  );
}
